/* 
 * @description: data structures and assistant methods for postil editing
 * @author: Jason Wong 
 * @since: 2020-06-02 10:38:33 
 */

import { uuid } from "@/utils/uuid";
import { DATA_ACTIONS } from "../../../data/consts";
import {isPostil, SYMBOLS} from './action';
import domHelper from '../util/dom';

const VIEW_MODE = {
  view: 0,
  edit: 1,
};

const EDIT_STATE = {
  adding: 0,
  editing: 1,
};

const RESULT_TYPE = {
  edited: 0,
  deleted: 1,
  canceled: 2,
};

const POSTIL_TYPE = {
  normal: "normal",
  good: "good",
  bad: "bad",
};

class ActionResult {
  constructor(actionType, postil) {
    this.actionType = actionType;
    this.data = postil;
  }
}

class BatchActionResult {
  constructor(actionType, batchKeyList, postil) {
    this.actionType = actionType;
    this.batchKeyList = batchKeyList;
    this.postil = postil;
  }
}

class PostilContext {
  constructor(el, qaList, postil, row) {
    this.el = el;
    this.qaList = qaList;
    this.postil = postil;
    this.row = row;
  }
}

const POSTIL_ACTION_TYPES = {
  none: 0,
  single: 1,
  batch: 2,
};

class BatchPostilContext {
  constructor(postilWord, qaList, postil, row) {
    this.postilWord = postilWord;
    this.qaList = qaList;
    this.postil = postil;
    this.row = row;
  }
}

const CANCELED_RESULT = new ActionResult(RESULT_TYPE.canceled);

class Postil {
  id;
  type;
  text;
  qaId;
  qaSubId;
  src;
  text;
  description;
  order;

  static empty() {
    let postil = new Postil();
    postil.id = postil.type = postil.qaId = postil.qaSubId = postil.src = postil.text = postil.description = "";
    postil.order = 0;

    return postil;
  }

  static newPostil(src) {
    let postil = new Postil();
    postil.type = POSTIL_TYPE.normal;
    postil.src = src;

    return postil;
  }
}
const EMPTY_POSTIL = Postil.empty();

function copyPostilContent(src, dst, ignoreFields) {
  for (const attr in EMPTY_POSTIL) {
    if (ignoreFields && ignoreFields.indexOf(attr) >= 0) continue;
    if (attr in src) dst[attr] = src[attr];
  }
}

function checkHasHighLightWord(html, word) {
  if (!html || !word || html.trim().length == 0 || word.trim().length == 0) return { highLightable: false };

  let temDiv = $(`<div>${html}</div>`)[0];
  let [highLightable, readonly] = [false, true];
  for (const node of temDiv.childNodes) {
    if (node.nodeName == "#text") {
      if (node.textContent.indexOf(word) >= 0) {
        highLightable = true;
        readonly = false;
        break;
      }
    } else if (node.nodeName == "SPAN") {
      if (node.classList && node.classList.contains("co") && node.innerText.indexOf(word) >= 0) {
        highLightable = true;
        if (node.innerText.length == word.length) {
          readonly = false;
          break;
        }
      }
    }
  }

  return { highLightable, readonly };
}

/**
 *
 * @param {Text} original
 * @param {String} word
 */
function formatTextWithHighlightWord(original, oldString) {
  const result = [];
  let text = original.textContent;
  let matchedPos = -1;
  while ((matchedPos = text.indexOf(oldString)) > -1) {
    if (matchedPos > 0) {
      result.push(document.createTextNode(text.substring(0, matchedPos)));
    }
    let newSpan = domHelper.createElementWithText("span", oldString);
    newSpan.classList.add("high-light", "color-unset");
    result.push(newSpan);
    text = text.substr(matchedPos + oldString.length);
  }
  if (text.length > 0) result.push(document.createTextNode(text));
  return result;
}

/**
 *
 * @param {String} html
 * @param {String} word
 * @param {DATA_ACTIONS.add|DATA_ACTIONS.edit|DATA_ACTIONS.delete} actionType
 */
function tryHighlightPostilRow(html, word, actionType) {
  let result = {
    highLightable: false,
    changable: false,
    formatedHtml: null,
  };

  let temDiv = $(`<div>${html}</div>`)[0];
  let currentNode = temDiv.hasChildNodes ? temDiv.firstChild : null;
  while (currentNode) {
    if (actionType == DATA_ACTIONS.add) {
      if (domHelper.isText(currentNode) && currentNode.textContent.indexOf(word) >= 0) {
        result.highLightable = true;
        formatTextWithHighlightWord(currentNode, word).forEach((el) => {
          currentNode.parentElement.insertBefore(el, currentNode);
        });
        const tempCurrentNode = currentNode;
        currentNode = currentNode.previousSibling;
        tempCurrentNode.remove();
      }
    } else {
      if (isPostil(currentNode) && currentNode.textContent == word) {
        result.highLightable = true;
        currentNode.classList.add("high-light","color-unset");
      }
    }
    currentNode = currentNode.nextSibling;
  }

  if (result.highLightable) {
    result.formatedHtml = temDiv.innerHTML;
  }
  return result;
}

function formatHtmlWithHighLightWord(html, word) {
  let temDiv = $(`<div>${html}</div>`)[0];
  let htmlRes = "";
  for (const node of temDiv.childNodes) {
    if (node.nodeName == "#text") {
      htmlRes += node.textContent.replace(word, `<span class="high-light">${word}</span>`);
    } else {
      htmlRes += node.outerHTML;
    }
  }
  return htmlRes;
}

/**
 * add postils or edit existing postils in gave row with special word
 * @param {String,String,Postil} param0
 */
function batchAddOrEditPostil({ row, word, postil }) {
  if (!row || !word || !postil) return;
  if (postil.type != POSTIL_TYPE.bad) {
    postil.qaId = postil.qaSubId = null;
  }
  if (!row.postils) row.postils = [];
  row.postils.$$each((p) => {
    if (p.type != POSTIL_TYPE.bad) {
      p.qaId = p.qaSubId = null;
    }
  });

  let temDiv = $(`<div>${row.html}</div>`)[0];
  let normalStartOrder = 1;
  let replacerIndex = 0;
  let replacerPrefix = `${uuid()}_replacer_`;
  let htmlRes = "";
  let existingPostilMap = {};
  for (const node of temDiv.childNodes) {
    if (node.nodeName == "#text") {
      let text = node.textContent;
      let curPosition = -1;
      let beginSearchPosition = 0;
      while ((curPosition = text.indexOf(word, beginSearchPosition)) > -1) {
        const replacedText = text.substring(0, curPosition) + `${replacerPrefix}${replacerIndex++}`;
        text = replacedText + text.substring(curPosition + word.length);
        beginSearchPosition = replacedText.length;
      }
      htmlRes += text;
    } else {
      /**
       * if a existing postil has the same word who equals to the replaced word:
       * 1. replace it with special replace symbol,like ${uuid()}_replacer_${index};
       * 2. update the associated postil's attributes and restore it into a map whose key is replacer's index
       */

      if (node.classList && node.classList.contains("co")) {
        const id = node.getAttribute("data-id");
        const associatedPostil = row.postils.$$first((p) => p.id == id);
        if (associatedPostil) {
          const postilWord = node.innerText;
          if (postilWord == word) {
            existingPostilMap[replacerIndex] = associatedPostil.id;
            htmlRes += `${replacerPrefix}${replacerIndex++}`;
            row.postils.$$remove(associatedPostil);
          } else {
            if (associatedPostil.type == "normal") {
              associatedPostil.order = normalStartOrder;
              node.setAttribute("data-order", associatedPostil.order);
              normalStartOrder++;
            }
            htmlRes += node.outerHTML;
          }
        }
      }
    }
  }

  if (replacerIndex > 0) {
    for (let i = 0; i < replacerIndex; i++) {
      let newPostil = {
        ...postil,
        id: uuid(),
        order: postil.type == "normal" ? normalStartOrder++ : 0,
      };
      const htmlOrderSegment = newPostil.type == "normal" ? ` data-order="${newPostil.order}"` : "";
      let replaceHTML = `<span class="co ${postil.type}" data-id="${newPostil.id}"${htmlOrderSegment}>${word}</span>`;
      htmlRes = htmlRes.replace(`${replacerPrefix}${i}`, replaceHTML);
      row.postils.push(newPostil);
    }
  }
  row.html = htmlRes;
}

function batchDeletePostil({ row, word, postil }) {
  if (!row.html || !row.postils || !word || !postil) return;

  let normalPostilIndex = 1;
  for (let i = 0; i < row.postils.length; i++) {
    let curPostil = row.postils[i];
    if (curPostil.src == word) {
      row.postils.splice(i, 1);
      i--;
    } else {
      if (curPostil.type == POSTIL_TYPE.normal) {
        curPostil.order = normalPostilIndex++;
      }
    }
  }

  let temDiv = $(`<div>${row.html}</div>`)[0];
  let editedHtml = "";
  for (const node of temDiv.childNodes) {
    if (node.nodeName == "SPAN" && node.classList.contains("co")) {
      const nodeContent = node.innerText;
      if (nodeContent == word) {
        editedHtml += node.innerText;
      } else {
        const dataId = node.getAttribute("data-id");
        const associatedPostil = row.postils.$$first((p) => p.id == dataId);
        if (associatedPostil) {
          node.className = `co ${associatedPostil.type}`;
          if (associatedPostil.type == POSTIL_TYPE.normal) {
            node.setAttribute("data-order", associatedPostil.order);
          } else {
            node.removeAttribute("data-order");
          }
        }
        editedHtml += node.outerHTML;
      }
    } else {
      let content = node.innerHTML ? node.innerHTML : node.textContent;
      if (content.length > 0) editedHtml += content;
    }
  }
  row.html = editedHtml;
}

function batchUpdatePostil({ action, row, word, postil }) {
  if (action == RESULT_TYPE.deleted) {
    batchDeletePostil({ row, word, postil });
  } else if (action == RESULT_TYPE.edited) {
    batchAddOrEditPostil({ row, word, postil });
  }
}

function addPostil({ row, postil, editingElementId }) {
  if (!row.html) return;
  if (!row.postils) row.postils = [];

  //true -> add postil;false -> update postil
  let isAdding = false;
  if (!postil.id) {
    postil.id == uuid();
    isAdding = true;
    row.postils.push(postil);
  } else {
    let updatingPostil = row.postils.$$first((p) => p.id == postil.id);
    if (updatingPostil) {
      for (const attr in EMPTY_POSTIL) {
        updatingPostil[attr] = postil[attr];
      }
    }
  }
  let normalPostilIndex = 1;
  row.postils.$$each((p) => {
    if (p.type == POSTIL_TYPE.normal) {
      p.order = normalPostilIndex++;
    }
  });

  let temDiv = $(`<div>${row.html}</div>`)[0];
  let editedHtml = "";
  for (const node of temDiv.childNodes) {
    if (node.nodeName == "SPAN" && node.classList.contains("co")) {
      const dataId = node.getAttribute("data-id");

      const associatedPostil = row.postils.$$first((p) => p.id == dataId);
      if (associatedPostil) {
        node.className = `co ${associatedPostil.type}`;
        if (associatedPostil.type == POSTIL_TYPE.normal) {
          node.setAttribute("data-order", associatedPostil.order);
        } else {
          node.removeAttribute("data-order");
        }
      }
      editedHtml += node.outerHTML;
    } else {
      if (
        node.classList &&
        node.classList.contains("sel") &&
        isAdding &&
        node.getAttribute("data-edite-id") == editingElementId
      ) {
        node.className = `co ${postil.type}`;
        node.setAttribute("data-id", postil.id);
        if (postil.type == POSTIL_TYPE.normal) node.setAttribute("data-order", postil.order);
        editedHtml += node.outerHTML;
      } else {
        let content = node.innerHTML ? node.innerHTML : node.textContent;
        if (content.length > 0) editedHtml += content;
      }
    }
  }
  row.html = editedHtml;
}

function deletePostil({ row, id }) {
  if (!row.html || !row.postils || !id) return;

  let normalPostilIndex = 1;
  for (let i = 0; i < row.postils.length; i++) {
    let curPostil = row.postils[i];
    if (curPostil.id == id) {
      row.postils.splice(i, 1);
      i--;
    } else {
      if (curPostil.type == POSTIL_TYPE.normal) {
        curPostil.order = normalPostilIndex++;
      }
    }
  }

  let temDiv = $(`<div>${row.html}</div>`)[0];
  let editedHtml = "";
  for (const node of temDiv.childNodes) {
    if (node.nodeName == "SPAN" && node.classList.contains("co")) {
      const dataId = node.getAttribute("data-id");
      if (dataId == id) {
        editedHtml += node.innerText;
      } else {
        const associatedPostil = row.postils.$$first((p) => p.id == dataId);
        if (associatedPostil) {
          node.className = `co ${associatedPostil.type}`;
          if (associatedPostil.type == POSTIL_TYPE.normal) {
            node.setAttribute("data-order", associatedPostil.order);
          } else {
            node.removeAttribute("data-order");
          }
        }
        editedHtml += node.outerHTML;
      }
    } else {
      let content = node.innerHTML ? node.innerHTML : node.textContent;
      if (content.length > 0) editedHtml += content;
    }
  }
  row.html = editedHtml;
}

const POSTIL_PREFIX = "<span class='postil-prefix'>【批注】</span>";
function postilHtmlFormatter(answer) {
  if (!answer.html) return null;
  return POSTIL_PREFIX + answer.html;
}

export {
  VIEW_MODE,
  EDIT_STATE,
  RESULT_TYPE,
  ActionResult,
  BatchActionResult,
  PostilContext,
  CANCELED_RESULT,
  POSTIL_TYPE,
  Postil,
  EMPTY_POSTIL,
  copyPostilContent,
  POSTIL_ACTION_TYPES,
  BatchPostilContext,
  checkHasHighLightWord,
  formatHtmlWithHighLightWord,
  addPostil,
  deletePostil,
  batchAddOrEditPostil,
  batchUpdatePostil,
  postilHtmlFormatter,
  tryHighlightPostilRow,
};
